import { formatCurrency, formatMilliseconds } from '../Utils';
import { PRESENTATION_THEMES, RANKING_OPTIONS } from './exportUtils';

/**
 * Generate presentation HTML template
 * @param {Object} config - Export configuration
 * @param {Array} filteredAgents - Filtered agent data
 * @param {Date} startDate - Start date filter
 * @param {Date} endDate - End date filter
 * @returns {string} HTML template string
 */
export const generatePresentationTemplate = (config, filteredAgents, startDate, endDate) => {
  const theme = PRESENTATION_THEMES[config.theme];

  return `
    <div style="
      background-color: ${theme.backgroundColor};
      color: ${theme.textColor};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 40px;
      min-height: 100vh;
    ">
      ${generateHeader(config, theme, startDate, endDate)}
      ${generateSummaryStats(config, theme, filteredAgents)}
      ${generateTopPerformers(config, theme, filteredAgents)}
      ${generateLeaderboardTable(config, theme, filteredAgents)}
    </div>
  `;
};

/**
 * Generate header section
 */
const generateHeader = (config, theme, startDate, endDate) => {
  return `
    <div style="
      border-bottom: 3px solid ${theme.primaryColor};
      padding-bottom: 20px;
      margin-bottom: 30px;
    ">
      <h1 style="
        color: ${theme.primaryColor};
        font-size: 2.5rem;
        font-weight: 700;
        margin-bottom: 10px;
      ">
        ${config.customTitle || 'Agent Leaderboard Report'}
      </h1>
      <p style="
        color: ${theme.secondaryColor};
        font-size: 1.2rem;
        margin-bottom: 5px;
      ">
        Performance Analysis - Ranked by ${RANKING_OPTIONS[config.ranking]?.label}
      </p>
      <p style="color: #6c757d; margin-bottom: 0;">
        Report Period: ${startDate ? startDate.toLocaleDateString() : 'All Time'} - ${endDate ? endDate.toLocaleDateString() : 'Present'}
      </p>
      <p style="color: #6c757d; font-size: 0.9rem;">
        Generated on ${new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })}
      </p>
    </div>
  `;
};

/**
 * Generate summary statistics section
 */
const generateSummaryStats = (config, theme, filteredAgents) => {
  const totalDeals = filteredAgents.reduce((sum, agent) => sum + (agent.dealsCompleted || 0), 0);
  const totalValue = filteredAgents.reduce((sum, agent) => sum + (agent.dealValue || 0), 0);
  const totalCommission = filteredAgents.reduce((sum, agent) => sum + (agent.commissionValue || 0), 0);
  const totalListings = filteredAgents.reduce((sum, agent) => sum + (agent.listingsAcquired || 0), 0);

  return `
    <div style="margin-bottom: 40px;">
      <h2 style="
        color: ${theme.primaryColor};
        font-size: 1.8rem;
        font-weight: 600;
        margin-bottom: 20px;
        border-left: 4px solid ${theme.primaryColor};
        padding-left: 15px;
      ">Performance Summary</h2>
      <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 20px;">
        ${generateStatCard(theme, totalDeals, 'Total Deals')}
        ${generateStatCard(theme, formatCurrency(totalValue), 'Total Deal Value')}
        ${generateStatCard(theme, formatCurrency(totalCommission), 'Total Commission')}
        ${generateStatCard(theme, totalListings, 'Total Listings')}
      </div>
    </div>
  `;
};

/**
 * Generate individual stat card
 */
const generateStatCard = (theme, value, label) => {
  return `
    <div style="
      background: ${theme.accentColor};
      padding: 20px;
      border-radius: 8px;
      text-align: center;
      border: 1px solid #e9ecef;
    ">
      <h3 style="color: ${theme.primaryColor}; margin-bottom: 5px;">
        ${value}
      </h3>
      <p style="margin: 0; color: #6c757d;">${label}</p>
    </div>
  `;
};

/**
 * Generate top performers section
 */
const generateTopPerformers = (config, theme, filteredAgents) => {
  if (!config.includeTopPerformers) return '';

  const topAgents = filteredAgents.slice(0, Math.min(3, config.topCount));

  return `
    <div style="margin-bottom: 40px;">
      <h2 style="
        color: ${theme.primaryColor};
        font-size: 1.8rem;
        font-weight: 600;
        margin-bottom: 20px;
        border-left: 4px solid ${theme.primaryColor};
        padding-left: 15px;
      ">Top ${config.topCount} Performers</h2>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px;">
        ${topAgents.map((agent, index) => generateTopPerformerCard(config, theme, agent, index)).join('')}
      </div>
    </div>
  `;
};

/**
 * Generate individual top performer card
 */
const generateTopPerformerCard = (config, theme, agent, index) => {
  const rankingOption = RANKING_OPTIONS[config.ranking];
  let rankingValue;

  if (rankingOption?.field === 'dealValue' || rankingOption?.field === 'commissionValue') {
    rankingValue = formatCurrency(agent[rankingOption.field] || 0);
  } else if (rankingOption?.field === 'avgTimeToFirstAction') {
    rankingValue = formatMilliseconds(agent[rankingOption.field] || 0, ['hours', 'minutes'], { trimDays: true, fullWords: false });
  } else {
    rankingValue = agent[rankingOption?.field] || 0;
  }

  return `
    <div style="
      background: ${theme.accentColor};
      padding: 20px;
      border-radius: 8px;
      text-align: center;
      border: 1px solid #e9ecef;
    ">
      <div style="
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: ${theme.primaryColor};
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 15px;
        font-weight: bold;
        font-size: 1.2rem;
      ">#${index + 1}</div>
      <h4 style="margin-bottom: 5px; color: ${theme.textColor};">
        ${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}
      </h4>
      <p style="color: #6c757d; font-size: 0.9rem; margin-bottom: 15px;">
        ${agent.agent?.user?.email || ''}
      </p>
      <div style="margin-bottom: 15px;">
        <strong style="color: ${theme.primaryColor}; font-size: 1.1rem;">
          ${rankingValue}
        </strong>
        <div style="color: #6c757d; font-size: 0.8rem;">${rankingOption?.label}</div>
      </div>
      <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; font-size: 0.8rem;">
        <div>
          <div style="color: #6c757d;">Deals</div>
          <strong>${agent.dealsCompleted || 0}</strong>
        </div>
        <div>
          <div style="color: #6c757d;">Listings</div>
          <strong>${agent.listingsAcquired || 0}</strong>
        </div>
        <div>
          <div style="color: #6c757d;">Score</div>
          <strong>${agent.totalPoints || 0}</strong>
        </div>
      </div>
    </div>
  `;
};

/**
 * Generate leaderboard table section
 */
const generateLeaderboardTable = (config, theme, filteredAgents) => {
  if (!config.includeFullLeaderboard) return '';

  const tableAgents = filteredAgents.slice(0, 20);

  return `
    <div style="margin-bottom: 40px;">
      <h2 style="
        color: ${theme.primaryColor};
        font-size: 1.8rem;
        font-weight: 600;
        margin-bottom: 20px;
        border-left: 4px solid ${theme.primaryColor};
        padding-left: 15px;
      ">Complete Leaderboard</h2>
      <div style="
        background: white;
        border-radius: 8px;
        overflow: hidden;
        border: 1px solid #e9ecef;
      ">
        <table style="width: 100%; border-collapse: collapse;">
          <thead style="background: ${theme.accentColor};">
            <tr>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Rank</th>
              <th style="padding: 12px; text-align: left; border-bottom: 1px solid #dee2e6;">Agent</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Deals</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Deal Value</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Commission</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Listings</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Score</th>
              <th style="padding: 12px; text-align: center; border-bottom: 1px solid #dee2e6;">Response Time</th>
            </tr>
          </thead>
          <tbody>
            ${tableAgents.map((agent, index) => generateTableRow(theme, agent, index)).join('')}
          </tbody>
        </table>
      </div>
    </div>
  `;
};

/**
 * Generate individual table row
 */
const generateTableRow = (theme, agent, index) => {
  return `
    <tr style="border-bottom: 1px solid #f8f9fa;">
      <td style="padding: 10px; text-align: center;">
        <span style="
          background: ${index < 3 ? theme.primaryColor : '#6c757d'};
          color: white;
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: bold;
        ">#${index + 1}</span>
      </td>
      <td style="padding: 10px;">
        <div>
          <div style="font-weight: 500; margin-bottom: 2px;">
            ${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}
          </div>
          <div style="color: #6c757d; font-size: 0.8rem;">
            ${agent.agent?.user?.email || ''}
          </div>
        </div>
      </td>
      <td style="padding: 10px; text-align: center;">${agent.dealsCompleted || 0}</td>
      <td style="padding: 10px; text-align: center;">${formatCurrency(agent.dealValue || 0)}</td>
      <td style="padding: 10px; text-align: center;">${formatCurrency(agent.commissionValue || 0)}</td>
      <td style="padding: 10px; text-align: center;">${agent.listingsAcquired || 0}</td>
      <td style="padding: 10px; text-align: center;">
        <strong style="color: ${theme.primaryColor};">
          ${agent.totalPoints || 0}
        </strong>
      </td>
      <td style="padding: 10px; text-align: center;">
        ${formatMilliseconds(agent.avgTimeToFirstAction || 0, ['h', 'm'], { trimDays: true, fullWords: false })}
      </td>
    </tr>
  `;
};

/**
 * Generate compact presentation template for smaller exports
 * @param {Object} config - Export configuration
 * @param {Array} filteredAgents - Filtered agent data
 * @param {Date} startDate - Start date filter
 * @param {Date} endDate - End date filter
 * @returns {string} Compact HTML template string
 */
export const generateCompactPresentationTemplate = (config, filteredAgents, startDate, endDate) => {
  const theme = PRESENTATION_THEMES[config.theme];
  const topAgents = filteredAgents.slice(0, config.topCount || 10);

  return `
    <div style="
      background-color: ${theme.backgroundColor};
      color: ${theme.textColor};
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      padding: 30px;
      max-width: 800px;
    ">
      <div style="text-align: center; margin-bottom: 30px;">
        <h1 style="
          color: ${theme.primaryColor};
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 10px;
        ">
          ${config.customTitle || 'Agent Leaderboard'}
        </h1>
        <p style="color: ${theme.secondaryColor}; margin-bottom: 5px;">
          Ranked by ${RANKING_OPTIONS[config.ranking]?.label}
        </p>
        <p style="color: #6c757d; font-size: 0.9rem;">
          ${startDate ? startDate.toLocaleDateString() : 'All Time'} - ${endDate ? endDate.toLocaleDateString() : 'Present'}
        </p>
      </div>

      <div style="
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
        gap: 15px;
      ">
        ${topAgents.map((agent, index) => generateCompactAgentCard(config, theme, agent, index)).join('')}
      </div>

      <div style="
        margin-top: 30px;
        text-align: center;
        color: #6c757d;
        font-size: 0.8rem;
      ">
        Generated on ${new Date().toLocaleDateString()}
      </div>
    </div>
  `;
};

/**
 * Generate compact agent card
 */
const generateCompactAgentCard = (config, theme, agent, index) => {
  const rankingOption = RANKING_OPTIONS[config.ranking];
  let rankingValue;

  if (rankingOption?.field === 'dealValue' || rankingOption?.field === 'commissionValue') {
    rankingValue = formatCurrency(agent[rankingOption.field] || 0);
  } else if (rankingOption?.field === 'avgTimeToFirstAction') {
    rankingValue = formatMilliseconds(agent[rankingOption.field] || 0, ['h', 'm'], { trimDays: true, fullWords: false });
  } else {
    rankingValue = agent[rankingOption?.field] || 0;
  }

  return `
    <div style="
      background: ${theme.accentColor};
      padding: 15px;
      border-radius: 8px;
      border: 1px solid #e9ecef;
      position: relative;
    ">
      <div style="
        position: absolute;
        top: -10px;
        left: 15px;
        background: ${theme.primaryColor};
        color: white;
        padding: 5px 10px;
        border-radius: 12px;
        font-size: 0.8rem;
        font-weight: bold;
      ">#${index + 1}</div>

      <div style="margin-top: 10px;">
        <h4 style="margin-bottom: 5px; color: ${theme.textColor}; font-size: 1rem;">
          ${agent.agent?.user?.firstName || ''} ${agent.agent?.user?.lastName || ''}
        </h4>
        <p style="color: #6c757d; font-size: 0.8rem; margin-bottom: 10px;">
          ${agent.agent?.user?.email || ''}
        </p>

        <div style="margin-bottom: 10px;">
          <strong style="color: ${theme.primaryColor}; font-size: 1.1rem;">
            ${rankingValue}
          </strong>
          <div style="color: #6c757d; font-size: 0.7rem;">${rankingOption?.label}</div>
        </div>

        <div style="display: flex; justify-content: space-between; font-size: 0.7rem;">
          <span>Deals: <strong>${agent.dealsCompleted || 0}</strong></span>
          <span>Listings: <strong>${agent.listingsAcquired || 0}</strong></span>
          <span>Score: <strong>${agent.totalPoints || 0}</strong></span>
        </div>
      </div>
    </div>
  `;
};
